import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { User, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabase";
import styles from "./onboarding.module.css";

interface ExperienceStepProps {
	onNext: (data: any) => void;
	onBack: () => void;
	initialData?: any;
}

export const ExperienceStep = ({
	onNext,
	onBack,
	initialData,
}: ExperienceStepProps) => {
	const [currentTitle, setCurrentTitle] = useState(
		initialData?.currentTitle || "",
	);
	const [yearsExperience, setYearsExperience] = useState(
		initialData?.yearsExperience || "",
	);
	const [loading, setLoading] = useState(false);

	const handleYearsExperienceChange = (value: string) => {
		// Only allow numbers and max 2 digits
		const numericValue = value.replace(/\D/g, "");
		if (numericValue.length <= 2) {
			setYearsExperience(numericValue);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!currentTitle.trim()) {
			alert("Please enter your current title");
			return;
		}

		if (!yearsExperience.trim()) {
			alert("Please enter your years of experience");
			return;
		}

		setLoading(true);

		try {
			// Call the edge function to save progress - saving title and years experience
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-doula-profile`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						step: "experience",
						data: {
							experience: {
								title: currentTitle,
								years_experience: parseInt(yearsExperience),
							},
						},
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || "Failed to save experience");
			}

			onNext({ currentTitle, yearsExperience });
		} catch (error) {
			console.error("Error saving experience:", error);
			alert("Failed to save experience. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6 }}
			className={styles.wrapper}
		>
			{/* Progress Bar */}
			<div className={styles.progressSection}>
				<div className={styles.progressHeader}>
					<span className={styles.progressLabel}>Step 4 of 15</span>
					<span className={styles.progressLabel}>Professional Information</span>
				</div>
				<div className={styles.progressTrack}>
					<div className={styles.progressFill} style={{ width: "30%" }} />
				</div>
			</div>

			{/* Main Card */}
			<Card className={styles.card}>
				<CardHeader className="text-center pb-4">
					<CardTitle className={styles.cardTitle}>
						<User className={styles.cardIcon} />
						Professional Information
					</CardTitle>
					<p className={styles.cardSubtitle}>
						Set your professional title and years of experience that will be
						displayed to families.
					</p>
				</CardHeader>

				<CardContent className="space-y-6">
					<form onSubmit={handleSubmit} className="space-y-6">
						<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
							{/* Professional Title */}
							<div className={`md:col-span-2 ${styles.innerPanel}`}>
								<div className="flex items-center mb-2">
									<User className="h-5 w-5 text-newTheme-spruce mr-2" />
									<label className={styles.fieldLabel}>Professional Title</label>
								</div>
								<Input
									placeholder="e.g., Certified Birth Doula, Postpartum Specialist"
									value={currentTitle}
									onChange={(e) => setCurrentTitle(e.target.value)}
									className="font-karla"
									required
								/>
								<p className={styles.helperText}>
									This title will be displayed on your listing for families to see
								</p>
							</div>

							{/* Years Experience */}
							<div className={styles.innerPanel}>
								<div className="flex items-center mb-2">
									<Clock className="h-5 w-5 text-newTheme-spruce mr-2" />
									<label className={styles.fieldLabel}>Years Experience</label>
								</div>
								<Input
									type="number"
									min="0"
									max="99"
									placeholder="0"
									value={yearsExperience}
									onChange={(e) => handleYearsExperienceChange(e.target.value)}
									className="font-karla"
									required
								/>
								<p className={styles.helperText}>Total years as a doula</p>
							</div>
						</div>

						{/* Navigation Buttons */}
						<div className={styles.navRow}>
							<Button
								type="button"
								variant="outline"
								onClick={onBack}
								className={styles.backButton}
							>
								Back
							</Button>
							<Button
								type="submit"
								disabled={loading}
								className={styles.submitButton}
							>
								{loading ? (
									<div className="flex items-center">
										<div className={styles.spinner} />
										Saving...
									</div>
								) : (
									"Continue"
								)}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</motion.div>
	);
};
